import { useEffect, useRef, useState } from "react";
import { type Toast, useToast } from "../../stores/toast";
import { formatTime } from "../../lib/datetime";
import styles from "./ToastViewport.module.css";

const KIND_SYMBOLS: Record<string, string> = {
	success: "◈", // ◈
	error: "■",   // ■
	warning: "▲", // ▲
	info: "●",    // ●
};

const TERMINAL_MARKS: Record<NonNullable<Toast["progressState"]>, string> = {
	running: "",
	completed: "✓", // ✓
	failed: "✕",    // ✕
	skipped: "⏵",   // ⏵
};

function formatElapsed(startedAt: string): string {
	try {
		const seconds = Math.max(
			0,
			Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000),
		);
		if (seconds < 60) return `${seconds}s`;
		const m = Math.floor(seconds / 60);
		const s = seconds % 60;
		return `${m}m${s.toString().padStart(2, "0")}s`;
	} catch {
		return "";
	}
}

/**
 * Sprint α-bis-2 (2026-05-09): sticky progress-mode line.
 *
 * Stays visible while ``t.progressState === "running"``. Shows a spinner
 * + elapsed timer + optional phase label + optional determinate progress
 * bar. When the store calls ``markProgressDone`` the kind / progressState
 * flip; after the configured ``holdMs`` (default 2.5s) the store's
 * ``dismissByKey`` removes the toast.
 *
 * Click-to-dismiss is allowed even while running — defensive in case a
 * task hangs and never emits a terminal event.
 */
function HudProgressLine({ t, onDone }: { t: Toast; onDone: () => void }) {
	// Re-render every second so elapsed time stays current. Single
	// setInterval per running progress toast — cheap.
	const [, forceTick] = useState(0);
	useEffect(() => {
		if (t.progressState !== "running") return;
		const id = window.setInterval(() => forceTick((n) => n + 1), 1000);
		return () => window.clearInterval(id);
	}, [t.progressState]);

	const isRunning = t.progressState === "running";
	const isTerminal = !isRunning;
	const mark = isTerminal ? TERMINAL_MARKS[t.progressState ?? "completed"] : "";
	const elapsed = t.startedAt ? formatElapsed(t.startedAt) : "";

	const progressFraction =
		typeof t.progress === "number"
			? Math.max(0, Math.min(1, t.progress))
			: null;

	return (
		<div
			className={`${styles.hudLine} ${styles.hudProgressLine} ${styles[t.kind]} ${styles.enter}`}
			role={t.kind === "error" ? "alert" : "status"}
			onClick={onDone}
		>
			<div className={styles.hudProgressRow}>
				<span className={styles.hudPrefix}>[SYS]</span>
				{isRunning ? (
					<span className={styles.hudSpinner} aria-hidden="true" />
				) : (
					<span className={styles.hudDoneMark} aria-hidden="true">
						{mark}
					</span>
				)}
				<span className={styles.hudText}>
					{t.message ? `${t.title} — ${t.message}` : t.title}
				</span>
				{elapsed && <span className={styles.hudElapsed}>{elapsed}</span>}
			</div>
			{t.phase && isRunning && (
				<div className={styles.hudPhase}>{t.phase}</div>
			)}
			{progressFraction !== null && (
				<div className={styles.hudProgressBar}>
					<div
						className={styles.hudProgressFill}
						style={{ width: `${Math.round(progressFraction * 100)}%` }}
					/>
				</div>
			)}
			{progressFraction === null && isRunning && (
				<div className={styles.hudProgressBar}>
					<div
						className={`${styles.hudProgressFill} ${styles.hudProgressFillIndeterminate}`}
					/>
				</div>
			)}
		</div>
	);
}

/** Single HUD line that streams in like text on glass. */
function HudLine({ t, onDone, index, total }: { t: Toast; onDone: () => void; index: number; total: number }) {
	const [phase, setPhase] = useState<"waiting" | "enter" | "hold" | "exit">("waiting");
	const symbol = KIND_SYMBOLS[t.kind] ?? "●";

	useEffect(() => {
		// Stagger entrance: each line waits index * 800ms
		const staggerDelay = index * 800;

		// Hold time: all lines wait until the last one has entered + 6s of reading
		const lastEntryTime = (total - 1) * 800;
		const holdEnd = lastEntryTime + 6000;

		// Exit sequentially: oldest (index 0) exits first, then next 1s later, etc.
		const exitStart = holdEnd + index * 1000;

		const enterTimer = setTimeout(() => setPhase("enter"), staggerDelay);
		const holdTimer = setTimeout(() => setPhase("hold"), staggerDelay + 600);
		const exitTimer = setTimeout(() => setPhase("exit"), exitStart);
		const removeTimer = setTimeout(onDone, exitStart + 1200);

		return () => {
			clearTimeout(enterTimer);
			clearTimeout(holdTimer);
			clearTimeout(exitTimer);
			clearTimeout(removeTimer);
		};
	}, [t.durationMs, onDone, index, total]);

	const line = t.message
		? `${symbol} ${t.title} — ${t.message}`
		: `${symbol} ${t.title}`;

	if (phase === "waiting") return null;

	return (
		<div
			className={`${styles.hudLine} ${styles[t.kind]} ${styles[phase]}`}
			role={t.kind === "error" ? "alert" : "status"}
		>
			<span className={styles.hudPrefix}>[SYS]</span>
			<span className={styles.hudText}>{line}</span>
			<span className={styles.hudTimestamp}>
				{formatTime(new Date().toISOString())}
			</span>
		</div>
	);
}

/**
 * Glass HUD overlay — text rolls in center-screen like data
 * streaming on a transparent display. Lines stagger in one by one,
 * earlier ones fade slowly while new ones appear below. Sticky
 * progress-mode toasts (Sprint α-bis-2) render via HudProgressLine
 * with their own elapsed timer + spinner.
 */
export function ToastViewport() {
	const toasts = useToast((s) => s.toasts);
	const dismiss = useToast((s) => s.dismiss);
	// Track the order each toast entered so stagger index is stable
	const orderRef = useRef<Map<number, number>>(new Map());
	const counterRef = useRef(0);

	// Assign a stable stagger index to each toast on first render
	for (const t of toasts) {
		if (!orderRef.current.has(t.id)) {
			orderRef.current.set(t.id, counterRef.current++);
		}
	}
	// Clean up removed toasts
	const activeIds = new Set(toasts.map((t) => t.id));
	for (const id of orderRef.current.keys()) {
		if (!activeIds.has(id)) orderRef.current.delete(id);
	}

	// Split into transient + progress sets so each gets the right
	// rendering path. Progress toasts don't participate in the
	// staggered entry/exit choreography — they're sticky.
	const transientToasts = toasts.filter((t) => !t.progressKey);
	const progressToasts = toasts.filter((t) => !!t.progressKey);

	const transientOrders = transientToasts.map((t) => orderRef.current.get(t.id) ?? 0);
	const minOrder = transientOrders.length ? Math.min(...transientOrders) : 0;

	if (toasts.length === 0) {
		return <div className={styles.viewport} aria-live="polite" aria-atomic="false" />;
	}

	return (
		<div className={styles.viewport} aria-live="polite" aria-atomic="false">
			<div className={styles.hudContainer}>
				{progressToasts.map((t) => (
					<HudProgressLine
						key={t.id}
						t={t}
						onDone={() => dismiss(t.id)}
					/>
				))}
				{transientToasts.map((t) => {
					const relIndex = (orderRef.current.get(t.id) ?? 0) - minOrder;
					return (
						<HudLine
							key={t.id}
							t={t}
							index={relIndex}
							total={transientToasts.length}
							onDone={() => dismiss(t.id)}
						/>
					);
				})}
			</div>
		</div>
	);
}
